'use client';

import { createClient } from '@/lib/supabase/client';

export interface DraftData {
  title: string;
  body: string;
  eraId: string;
  topicId: string;
  region: string;
  certaintyLabel: string;
  isAnonymous: boolean;
  citations: any[];
  selectedArchiveId: string | null;
  isNewArchive: boolean;
  newArchiveTitle: string;
}

export interface DraftRow {
  id: string;
  lastSavedAt: string;
  title: string;
  body: string;
  eraId: string;
  topicId: string;
  region: string;
  certaintyLabel: string;
  isAnonymous: boolean;
  citations: any[];
  selectedArchiveId: string | null;
  isNewArchive: boolean;
  newArchiveTitle: string;
}

function mapDraftRow(row: any): DraftRow {
  return {
    id: row.id,
    lastSavedAt: row.last_saved_at,
    title: row.title || '',
    body: row.body || '',
    eraId: row.era_id || '',
    topicId: row.topic_id || '',
    region: row.region || '',
    certaintyLabel: row.certainty_label || '',
    isAnonymous: row.is_anonymous || false,
    citations: row.citations || [],
    selectedArchiveId: row.selected_archive_id || null,
    isNewArchive: row.is_new_archive || false,
    newArchiveTitle: row.new_archive_title || '',
  };
}

/**
 * Save or update the user's current draft.
 * Each user has at most one active draft (upsert by user_id).
 */
export async function saveDraftToSupabase(data: DraftData): Promise<string | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  try {
    // Check if a draft already exists for this user
    const { data: existing } = await supabase
      .from('story_drafts')
      .select('id')
      .eq('user_id', user.id)
      .order('last_saved_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      // Update existing draft
      const { error } = await supabase
        .from('story_drafts')
        .update({
          title: data.title,
          body: data.body,
          era_id: data.eraId || null,
          topic_id: data.topicId || null,
          region: data.region || null,
          certainty_label: data.certaintyLabel || null,
          is_anonymous: data.isAnonymous,
          citations: data.citations,
          selected_archive_id: data.selectedArchiveId || null,
          is_new_archive: data.isNewArchive,
          new_archive_title: data.newArchiveTitle || null,
          last_saved_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      if (error) {
        console.error('saveDraftToSupabase update error:', error.message);
        return null;
      }
      return existing.id;
    } else {
      // Insert new draft
      const { data: inserted, error } = await supabase
        .from('story_drafts')
        .insert({
          user_id: user.id,
          title: data.title,
          body: data.body,
          era_id: data.eraId || null,
          topic_id: data.topicId || null,
          region: data.region || null,
          certainty_label: data.certaintyLabel || null,
          is_anonymous: data.isAnonymous,
          citations: data.citations,
          selected_archive_id: data.selectedArchiveId || null,
          is_new_archive: data.isNewArchive,
          new_archive_title: data.newArchiveTitle || null,
        })
        .select('id')
        .single();

      if (error) {
        console.error('saveDraftToSupabase insert error:', error.message);
        return null;
      }
      return inserted?.id || null;
    }
  } catch (err: any) {
    console.error('saveDraftToSupabase error:', err.message);
    return null;
  }
}

/**
 * Load the most recent draft for the current user.
 */
export async function loadDraftFromSupabase(): Promise<DraftRow | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  try {
    const { data, error } = await supabase
      .from('story_drafts')
      .select('*')
      .eq('user_id', user.id)
      .order('last_saved_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('loadDraftFromSupabase error:', error.message);
      return null;
    }
    return data ? mapDraftRow(data) : null;
  } catch (err: any) {
    console.error('loadDraftFromSupabase error:', err.message);
    return null;
  }
}

/**
 * Delete the user's draft after successful submission.
 */
export async function deleteDraftFromSupabase(): Promise<void> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  try {
    await supabase
      .from('story_drafts')
      .delete()
      .eq('user_id', user.id);
  } catch (err: any) {
    console.error('deleteDraftFromSupabase error:', err.message);
  }
}
